/**
 * Unified entry point for the consolidated frontend.
 */

import { ThemeProvider } from "@adafia/ui";
import {
	createReactRouterV7Options,
	getWebInstrumentations,
	initializeFaro,
	ReactIntegration,
} from "@grafana/faro-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
	createRoutesFromChildren,
	matchRoutes,
	Routes,
	useLocation,
	useNavigationType,
} from "react-router-dom";

import PortfolioApp from "./App";
import NotesApp from "./notes/App";
import ResumeApp from "./resume/App";

const faroUrl = process.env.BUN_PUBLIC_FARO_URL;
let isTelemetryStarted = false;

function startTelemetry() {
	if (!faroUrl || isTelemetryStarted) return;
	isTelemetryStarted = true;

	initializeFaro({
		url: faroUrl,
		app: {
			name: "adafia-io-web",
			version: "0.1.0",
			environment: process.env.NODE_ENV || "production",
		},
		instrumentations: [
			...getWebInstrumentations(),
			new ReactIntegration({
				router: createReactRouterV7Options({
					createRoutesFromChildren,
					matchRoutes,
					// biome-ignore lint/style/useNamingConvention: React Router v7 API requires uppercase Routes component reference
					Routes,
					useLocation,
					useNavigationType,
				}),
			}),
		],
	});
}

// ePrivacy / GDPR compliant conditional initialization
const hasConsent =
	typeof localStorage !== "undefined" &&
	localStorage.getItem("adafia-consent") === "granted";
if (hasConsent) {
	startTelemetry();
} else {
	window.addEventListener("adafia-consent-granted", startTelemetry);
}

const queryClient = new QueryClient();

const elem = document.getElementById("root");
if (!elem) throw new Error("Root element not found");

const path = window.location.pathname;

let ActiveApp = PortfolioApp;
if (path.startsWith("/notes")) {
	ActiveApp = NotesApp;
} else if (path.startsWith("/resumes")) {
	ActiveApp = ResumeApp;
}

const app = (
	<StrictMode>
		<ThemeProvider>
			<QueryClientProvider client={queryClient}>
				<ActiveApp />
			</QueryClientProvider>
		</ThemeProvider>
	</StrictMode>
);

if (import.meta.hot) {
	if (!import.meta.hot.data.root) {
		import.meta.hot.data.root = createRoot(elem);
	}
	const root = import.meta.hot.data.root;
	root.render(app);
} else {
	createRoot(elem).render(app);
}
